import { useMutation, useQueryClient } from "@tanstack/react-query";

import type { CommentItem } from "@/entities/comment";
import { apiPatch, apiPost, qk } from "@/shared/api";

/**
 * Публикация, правка и удаление своего отзыва (доработки §6.1).
 *
 * Три мутации в одном файле, потому что у них одна зона инвалидации и одно
 * правило: после любой из них меняется и лента, и сводка на карточке робота
 * (счётчик, средняя оценка, `can_comment`). Инвалидация идёт по префиксу
 * `qk.robot(id)`, в который вложены ключи отзывов, — так одна строка
 * обновляет карточку и все три вкладки сортировки сразу.
 *
 * `Idempotency-Key` здесь не нужен, в отличие от денежных ручек: двойную
 * отправку закрывает уникальный индекс «один отзыв на робота» — второй запрос
 * получит 409 с id уже существующего отзыва, и экран откроет редактор.
 */
export interface CommentPayload {
  text: string;
  rating: number | null;
}

export function useCreateComment(robotId: number) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ text, rating }: CommentPayload) =>
      apiPost("/api/v1/robots/{robot_id}/comments", {
        params: { robot_id: robotId },
        body: { text, rating },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.robot(robotId) });
    },
  });
}

export function useEditComment(robotId: number, commentId: number) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ text, rating }: CommentPayload) =>
      apiPatch("/api/v1/comments/{comment_id}", {
        params: { comment_id: commentId },
        body: { text, rating },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.robot(robotId) });
    },
  });
}

export function useDeleteComment(robotId: number) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (commentId: number): Promise<CommentItem> =>
      apiPost("/api/v1/comments/{comment_id}/delete", {
        params: { comment_id: commentId },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.robot(robotId) });
    },
  });
}
